import React, { useEffect, useRef, useState } from "react";
import { X, Trash2, Camera, FileText, Bot, ExternalLink } from "lucide-react";
import { Button, Field, Spinner } from "./ui/kit";
import ZakazkaPicker from "./ZakazkaPicker";
import { fmtKc } from "../lib/format";
import { canSee } from "../lib/nav";
import { useAuth } from "../context/AuthContext";
import api, { apiError, souborUrl } from "../lib/api";
import Segment from "./Segment";
import Vyber from "./Vyber";

/** Společný obal — na mobilu spodní sheet, na PC vystředěné okno. */
export function Dialog({ title, meta, onClose, children, footer, wide, sirka, bare, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex md:items-center items-end justify-center md:p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white w-full ${sirka || (wide ? "md:max-w-[720px]" : "md:max-w-[460px]")}
        md:rounded-card rounded-t-sheet shadow-panel max-h-[92vh] flex flex-col`}>
        <div className="border-b border-line-softer px-5 py-4 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 className="text-[16px] font-800 text-ink truncate">{title}</h2>
            {meta && <div className="amount text-[11px] text-muted mt-0.5 truncate">{meta}</div>}
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink shrink-0" data-testid={`${testid}-close`}>
            <X size={20} />
          </button>
        </div>
        <div className={bare ? "overflow-y-auto" : "p-5 space-y-4 overflow-y-auto"}>{children}</div>
        {footer && <div className="border-t border-line-softer px-5 py-3 flex items-center gap-2 shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

function Chyba({ text, testid }) {
  if (!text) return null;
  return <div className="text-[12.5px] text-danger" data-testid={testid}>{text}</div>;
}

function SkladSelect({ sklady, value, onChange, label = "Sklad", testid }) {
  return (
    <Field label={label}>
      <Vyber className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">— vyber sklad —</option>
        {sklady.map((s) => (
          <option key={s.id} value={s.id}>{s.nazev}{s.typ === "DODAVKA" ? " (dodávka)" : ""}</option>
        ))}
      </Vyber>
    </Field>
  );
}

/**
 * Zařazení řádků přijatého dokladu: buď na zakázku, nebo na sklad — a při
 * skladu klidně na víc skladů najednou (centrála vs. dodávka).
 */
export function ZaraditDialog({ doklad, sklady, onClose, onSaved }) {
  const radky = doklad.radky || [];
  // Práce, doprava a pronájem se naskladnit nedají — na sklad se předvybere
  // jen materiál, zbytek si uživatel musí odkliknout sám.
  const materialove = radky.filter((r) => r.skladovatelne !== false).map((r) => r.id);
  const [kam, setKam] = useState("SKLAD");
  const [vybrane, setVybrane] = useState(() => (materialove.length ? materialove : radky.map((r) => r.id)));
  const [skladId, setSkladId] = useState(sklady[0]?.id || "");
  const [rozdelit, setRozdelit] = useState(false);
  const [kamRadek, setKamRadek] = useState({});           // radek_id -> sklad_id
  const [pripadId, setPripadId] = useState(null);
  const [techId, setTechId] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const prepni = (id) => setVybrane((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const vseVybrano = vybrane.length === radky.length && radky.length > 0;
  const prepniVse = () => setVybrane(vseVybrano ? [] : radky.map((r) => r.id));
  const hodnota = radky.filter((r) => vybrane.includes(r.id)).reduce((s, r) => s + (r.celkem || 0), 0);
  const skladRadku = (id) => kamRadek[id] || skladId;

  const submit = async () => {
    if (!vybrane.length) return setErr("Vyber aspoň jeden řádek.");
    if (kam === "SKLAD" && !rozdelit && !skladId) return setErr("Vyber sklad.");
    if (kam === "SKLAD" && rozdelit && vybrane.some((id) => !skladRadku(id)))
      return setErr("U každého řádku vyber sklad.");
    if (kam === "ZAKAZKA" && !pripadId && !techId) return setErr("Vyber zakázku.");
    setErr(""); setSaving(true);
    try {
      const payload = kam === "SKLAD" && rozdelit
        ? { prirazeni: vybrane.map((id) => ({ radek_id: id, sklad_id: skladRadku(id) })) }
        : {
          radky_ids: vybrane,
          sklad_id: kam === "SKLAD" ? skladId : null,
          obchodni_pripad_id: kam === "ZAKAZKA" ? pripadId : null,
          technologie_id: kam === "ZAKAZKA" ? techId : null,
        };
      await api.post(`/api/doklady/${doklad.faktura_id}/zaradit`, payload);
      onSaved(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title={`Zařadit ${doklad.cislo || "doklad"}`} onClose={onClose} wide testid="zaradit-dialog"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Zrušit</Button>
        <span className="text-[12px] text-muted ml-2 hidden md:inline">
          {vybrane.length} z {radky.length} řádků · <span className="amount">{fmtKc(hodnota)} Kč</span>
        </span>
        <Button className="ml-auto" onClick={submit} disabled={saving} data-testid="zaradit-ulozit">
          {saving ? <Spinner /> : "Zařadit"}
        </Button>
      </>}>
      <Segment className="flex gap-1 p-1 bg-line-soft rounded-pill w-fit">
        {[["SKLAD", "Na sklad"], ["ZAKAZKA", "Rovnou na zakázku"]].map(([v, l]) => (
          <button data-on={kam === v} key={v}
            onClick={() => {
              setKam(v);
              // na zakázku patří i práce a doprava, na sklad jen materiál
              setVybrane(v === "ZAKAZKA" ? radky.map((r) => r.id)
                : (materialove.length ? materialove : radky.map((r) => r.id)));
            }}
            data-testid={`zaradit-kam-${v}`}
            className={`pill px-3.5 h-8 text-[12px] font-700 ${kam === v ? "text-white" : "text-sec"}`}>{l}</button>
        ))}
      </Segment>
      <p className="text-[11.5px] text-muted">
        {kam === "ZAKAZKA"
          ? "Materiál se hned započítá do nákladů zakázky."
          : "Materiál se na skladu ohlásí. Do zásoby ho pustí až naskladnění, nákladem zakázky se stane až výdejkou."}
      </p>

      {kam === "SKLAD" ? (
        <>
          <SkladSelect sklady={sklady} value={skladId} onChange={setSkladId}
            label={rozdelit ? "Výchozí sklad" : "Sklad"} testid="zaradit-sklad" />
          <label className="flex items-center gap-2 text-[12.5px] font-600 text-sec">
            <input type="checkbox" checked={rozdelit} onChange={() => setRozdelit((r) => !r)}
              data-testid="zaradit-rozdelit" />
            Rozdělit položky mezi víc skladů
          </label>
        </>
      ) : (
        <ZakazkaPicker pripadId={pripadId} technologieId={techId}
          onChange={(p, t) => { setPripadId(p); setTechId(t); }} testid="zaradit-zakazka" />
      )}

      <div className="border border-line-softer rounded-card overflow-hidden">
        <label className="flex items-center gap-3 px-4 py-2.5 bg-inputbg border-b border-line-softer cursor-pointer">
          <input type="checkbox" checked={vseVybrano} onChange={prepniVse} data-testid="zaradit-vse" />
          <span className="text-[12px] font-700 text-ink">Vybrat vše</span>
          <span className="ml-auto text-[11.5px] text-muted">
            {vybrane.length} z {radky.length} · <span className="amount">{fmtKc(hodnota)} Kč</span>
          </span>
        </label>
        {radky.map((r) => (
          <div key={r.id} data-testid={`zaradit-radek-${r.id}`}
            className="flex items-center gap-3 px-4 py-2.5 border-b border-line-softer last:border-0 hover:bg-inputbg">
            <input type="checkbox" checked={vybrane.includes(r.id)} onChange={() => prepni(r.id)} />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-600 text-ink truncate">{r.nazev || "—"}</span>
              <span className="block text-[11.5px] text-muted amount">
                {r.mnozstvi || 0} {r.mj || "ks"}
                {r.skladovatelne === false && (
                  <span className="label-up ml-2 px-1.5 py-0.5 rounded-chip bg-inputbg text-muted not-italic">
                    {r.typ === "PRACE" ? "práce" : "služba"} — nepatří na sklad
                  </span>
                )}
              </span>
            </span>
            {kam === "SKLAD" && rozdelit && (
              <Vyber className="field h-8 w-[150px] text-[12px] shrink-0" value={skladRadku(r.id)}
                data-testid={`zaradit-sklad-${r.id}`}
                onChange={(e) => setKamRadek((k) => ({ ...k, [r.id]: e.target.value }))}>
                {sklady.map((s) => <option key={s.id} value={s.id}>{s.nazev}</option>)}
              </Vyber>
            )}
            <span className="amount text-[12.5px] font-700 text-ink shrink-0">{fmtKc(r.celkem || 0)} Kč</span>
          </div>
        ))}
      </div>
      <Chyba text={err} testid="zaradit-chyba" />
    </Dialog>
  );
}

/**
 * Nový doklad na sklad — vyfotit nebo nahrát, klidně víc stran najednou.
 * Všechny soubory jdou do AI jako jeden dokument, takže z třístránkového
 * dodacího listu vznikne jeden doklad se všemi položkami.
 */
export function NovyDokladDialog({ onClose, onHotovo }) {
  const [soubory, setSoubory] = useState([]);
  const [stav, setStav] = useState("");
  const [err, setErr] = useState("");
  const fotoRef = useRef(null);
  const souborRef = useRef(null);

  const pridej = (e) => {
    const nove = Array.from(e.target.files || []);
    e.target.value = "";
    setSoubory((s) => [...s, ...nove]);
  };

  const nahraj = async () => {
    if (!soubory.length) return setErr("Přidej aspoň jednu fotku nebo PDF.");
    setErr(""); setStav("Nahrávám…");
    try {
      const fd = new FormData();
      soubory.forEach((f) => fd.append("file", f));
      // Doklad nahraný ze skladu je nákup — směr nenecháváme hádat AI.
      fd.append("smer", "PRIJATA");
      const { data: up } = await api.post("/api/doklady/upload", fd, { headers: { "Content-Type": undefined } });
      setStav("Čtu doklad…");
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: job } = await api.get(`/api/doklady/ocr-jobs/${up.job_id}`);
        if (job.status === "done") { onHotovo(up.faktura_id); onClose(); return; }
        if (job.status === "error") { setErr(job.detail || "Doklad se nepodařilo přečíst."); setStav(""); return; }
      }
      setErr("Čtení trvá moc dlouho. Zkus to znovu.");
      setStav("");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setStav("");
    }
  };

  return (
    <Dialog title="Nový doklad na sklad" onClose={onClose} testid="novy-doklad-dialog"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={!!stav}>Zrušit</Button>
        <Button className="ml-auto" onClick={nahraj} disabled={!!stav || !soubory.length}
          data-testid="doklad-precist">
          {stav ? <Spinner /> : `Přečíst ${soubory.length || ""}`}
        </Button>
      </>}>
      <input ref={fotoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pridej} />
      <input ref={souborRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" multiple className="hidden"
        onChange={pridej} data-testid="doklad-soubory" />
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => fotoRef.current?.click()} disabled={!!stav} data-testid="doklad-vyfotit"
          className="border border-dashed border-line-strong rounded-card py-6 flex flex-col items-center gap-1.5 hover:bg-inputbg">
          <Camera size={20} className="text-accent" />
          <span className="text-[12.5px] font-700 text-ink">Vyfotit</span>
          <span className="text-[10.5px] text-muted">i po stranách</span>
        </button>
        <button onClick={() => souborRef.current?.click()} disabled={!!stav} data-testid="doklad-nahrat"
          className="border border-dashed border-line-strong rounded-card py-6 flex flex-col items-center gap-1.5 hover:bg-inputbg">
          <FileText size={20} className="text-accent" />
          <span className="text-[12.5px] font-700 text-ink">Nahrát soubor</span>
          <span className="amount text-[10.5px] text-muted">PDF · JPG · PNG</span>
        </button>
      </div>

      {soubory.length > 0 && (
        <div className="border border-line-softer rounded-card overflow-hidden" data-testid="doklad-seznam">
          {soubory.map((f, i) => (
            <div key={i} className="flex items-center gap-3 px-3.5 py-2 border-b border-line-softer last:border-0">
              <span className="amount text-[11px] text-muted-2 w-5">{i + 1}.</span>
              <span className="min-w-0 flex-1 text-[12.5px] text-ink truncate">{f.name}</span>
              <button className="text-muted hover:text-danger" disabled={!!stav}
                onClick={() => setSoubory((s) => s.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {stav && (
        <div className="flex items-center gap-2 text-[12.5px] text-sec" data-testid="doklad-stav">
          <Bot size={16} className="text-accent" /> {stav}
        </div>
      )}
      <p className="text-[11.5px] text-muted">
        Víc fotek jednoho dokladu se přečte dohromady — vznikne jeden doklad se všemi položkami.
      </p>
      <Chyba text={err} testid="doklad-chyba" />
    </Dialog>
  );
}

const TYP_POHYBU = {
  PRIJEM: { label: "Příjem", cls: "bg-success-bg text-success" },
  VYDEJ: { label: "Výdej", cls: "bg-accent/10 text-accent-hover" },
  VRATKA: { label: "Vratka", cls: "bg-success-bg text-success" },
  PRESUN: { label: "Převodka", cls: "bg-inputbg text-sec" },
  INVENTURA: { label: "Inventura", cls: "bg-inputbg text-sec" },
};

/** Jeden řádek historie — odkud, kam, kdo a z jakého dokladu. */
export function PohybRadek({ p, onOtevri, onSmazat }) {
  const t = TYP_POHYBU[p.typ] || TYP_POHYBU.PRIJEM;
  return (
    <div className="px-4 py-3 border-b border-line-softer last:border-0" data-testid={`pohyb-${p.id}`}>
      <div className="flex items-center gap-2.5">
        <span className={`label-up px-2 py-1 rounded-pill shrink-0 ${t.cls}`}>{t.label}</span>
        {onOtevri ? (
          <button className="min-w-0 flex-1 text-left text-[13px] font-600 text-ink truncate hover:text-accent-hover"
            onClick={onOtevri}>{p.nazev || "—"}</button>
        ) : (
          <span className="min-w-0 flex-1 text-[13px] font-600 text-ink truncate">{p.nazev || "—"}</span>
        )}
        <span className="amount text-[13px] font-600 text-ink shrink-0">
          {p.typ === "VYDEJ" || p.typ === "PRESUN" ? "−" : "+"}{p.mnozstvi} <span className="text-[11px] text-muted-2">{p.mj}</span>
        </span>
        <span className="amount text-[13px] font-700 text-ink w-[100px] text-right shrink-0">{fmtKc(p.celkem)} Kč</span>
        {onSmazat && (
          <button onClick={onSmazat} className="text-muted hover:text-danger shrink-0"
            title="Stornovat pohyb" data-testid={`storno-${p.id}`}>
            <Trash2 size={15} />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-1 text-[11.5px] text-muted pl-1">
        <span>{p.datum}</span>
        <span>· {p.sklad_nazev || "—"}</span>
        {p.kdo && <span>· {p.kdo}</span>}
        {p.zakazka && <span>· {p.zakazka}</span>}
        {p.presun && <span>· {p.presun.z} → {p.presun.do}</span>}
        {p.stav === "CEKA" && (
          <span className="label-up px-1.5 py-0.5 rounded-chip bg-accent/10 text-accent-hover">čeká na naskladnění</span>
        )}
        {p.naskladnil && p.naskladnil !== p.kdo && <span>· naskladnil {p.naskladnil}</span>}
        {(p.doklad?.soubory || []).map((s, i) => (
          <a key={i} href={souborUrl(s.url)} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 font-700 text-accent-hover" data-testid={`pohyb-doklad-${p.id}`}>
            <ExternalLink size={11} /> {p.doklad.cislo || s.nazev}
          </a>
        ))}
        {p.doklad && !(p.doklad.soubory || []).length && <span>· doklad {p.doklad.cislo}</span>}
      </div>
    </div>
  );
}

/**
 * Detail skladové položky ve třech krocích, jak se na ni člověk ptá:
 * co mám k dispozici → kde se to použilo → co se s tím dělo.
 * Vpravo (na mobilu v záložce Nastavení) žije minimální zásoba — per sklad.
 */

const STAV_MINIMA = {
  ok: { text: "Nad minimem", chip: "V pořádku", c: "#17876a", bg: "#eaf6f1" },
  near: { text: "Blíží se minimu", chip: "Blíží se minimu", c: "#c98a13", bg: "#fbf3e2" },
  below: { text: "Pod minimem", chip: "Pod minimem", c: "#d93a2b", bg: "#fdeeec" },
};

/** Zrcadlo backendového `stav_minima` — jen pro okamžitou odezvu stepperu. */
export function stavMinima(stock, min, mj = "ks") {
  min = Number(min) || 0; stock = Number(stock) || 0;
  if (min <= 0) return null;
  const r = (x) => Math.round(x * 100) / 100;
  let status, diff;
  if (stock >= min) {
    status = stock < min * 1.25 ? "near" : "ok";
    diff = `+${r(stock - min)} nad minimem`;
  } else {
    status = "below";
    diff = `chybí ${r(min - stock)} ${mj}`;
  }
  const zaklad = Math.max(min * 2, 1);
  return { status, diff, min_zasoba: r(min),
    bar_pct: Math.min(100, Math.round((stock / zaklad) * 100)),
    min_mark_pct: Math.min(100, Math.round((min / zaklad) * 100)) };
}

function StavChip({ stav, testid }) {
  if (!stav) return null;
  const m = STAV_MINIMA[stav.status];
  return (
    <span className="label-up px-2.5 py-1 rounded-pill shrink-0" data-testid={testid}
      style={{ color: m.c, background: m.bg }}>{m.chip}</span>
  );
}

function StavRadek({ stav, extra, testid }) {
  if (!stav) return <div className="text-[11.5px] font-600 text-muted" data-testid={testid}>Minimum se nehlídá.</div>;
  const m = STAV_MINIMA[stav.status];
  return (
    <div className="flex items-center gap-1.5 text-[11.5px] font-600" style={{ color: m.c }} data-testid={testid}>
      <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: m.c }} />
      {m.text} · {stav.diff}{extra ? ` · ${extra}` : ""}
    </div>
  );
}

/** Pás zásoby: celá šířka = 2× minimum, svislá ryska = minimum. */
function ZasobaPas({ stav, mj }) {
  if (!stav) return null;
  const m = STAV_MINIMA[stav.status];
  return (
    <div className="card p-4" data-testid="zasoba-pas">
      <div className="relative">
        <div className="h-3 rounded-[6px] bg-[#e7e5e0] overflow-hidden">
          <div className="h-full rounded-[6px]" style={{ width: `${stav.bar_pct}%`, background: m.c }} />
        </div>
        <div className="absolute top-[-2px] h-4 w-[2px] bg-ink" style={{ left: `${stav.min_mark_pct}%` }} />
        <div className="amount text-[9.5px] font-600 text-ink mt-1"
          style={{ marginLeft: `max(0px, calc(${stav.min_mark_pct}% - 24px))` }}>
          MIN {stav.min_zasoba}
        </div>
      </div>
      <div className="text-[12px] font-600 mt-1" style={{ color: m.c }}>{stav.diff}</div>
    </div>
  );
}

/** Stepper minima — tlačítka −/+ a číslo přepisovatelné z klávesnice. */
function MinStepper({ value, onChange, krok, mj, velky, testid }) {
  const posun = (smer) => {
    const v = Number(value) || 0;
    // s balením se zaokrouhluje na celá balení
    const nove = Math.max(0, Math.round((v + smer * krok) / krok) * krok);
    onChange(nove);
  };
  const btn = velky
    ? "h-12 w-12 rounded-[14px] bg-white border border-line text-[18px]"
    : "h-7 w-7 rounded-[8px] bg-line-soft text-[15px]";
  return (
    <div className={`flex items-center gap-2 ${velky ? "" : "border border-line rounded-[10px] bg-white px-2 py-1.5"}`}>
      <button type="button" onClick={() => posun(-1)} data-testid={`${testid}-minus`}
        className={`${btn} font-700 text-ink flex items-center justify-center shrink-0 hover:bg-inputbg`}>−</button>
      <div className="flex-1 flex items-baseline justify-center gap-1.5 min-w-0">
        <input type="number" min="0" value={value} data-testid={`${testid}-input`}
          onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
          className={`amount font-700 text-ink text-center bg-transparent outline-none w-[76px]
            ${velky ? "text-[26px]" : "text-[18px]"}`} />
        <span className="text-[12px] font-500 text-sec-3 shrink-0">{mj}</span>
      </div>
      <button type="button" onClick={() => posun(1)} data-testid={`${testid}-plus`}
        className={`${btn} font-700 text-ink flex items-center justify-center shrink-0 hover:bg-inputbg`}>+</button>
    </div>
  );
}

function Prepinac({ on, onChange, velky, testid }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testid}
      className={`relative rounded-pill transition-colors shrink-0 ${velky ? "w-11 h-[26px]" : "w-9 h-5"}
        ${on ? "bg-accent" : "bg-[#e7e5e0]"}`}>
      <span className={`absolute top-[2px] rounded-full bg-white shadow-card transition-all
        ${velky ? "h-[22px] w-[22px]" : "h-4 w-4"} ${on ? (velky ? "left-[20px]" : "left-[18px]") : "left-[2px]"}`} />
    </button>
  );
}

export function PolozkaDetailDialog({ polozkaId, skladId, sklady = [], onClose, onPresun, onVratka, onZmena }) {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("dostupne");
  const [mobilTab, setMobilTab] = useState("sklad");
  const [err, setErr] = useState("");
  // nastavení zásoby (per sklad)
  const [sklNast, setSklNast] = useState("");
  const [form, setForm] = useState({ min: 0, hlidat: false, dodavatel: "", lhuta: "", baleni: "" });
  const [saving, setSaving] = useState(false);
  const [chybaNastaveni, setChybaNastaveni] = useState("");
  // Mazání je zásah do historie i do hodnoty majetku — patří jen majiteli.
  const muzeMazat = canSee(user, ["OWNER"]);

  const nacti = () => api.get(`/api/sklad/polozky/${polozkaId}`)
    .then(({ data }) => setD(data)).catch(() => setD(false));
  useEffect(() => { nacti(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [polozkaId]);

  // výchozí sklad pro nastavení: filtr stránky → sklad s největší zásobou → první
  useEffect(() => {
    if (!d || sklNast) return;
    const nej = (d.rozpad || [])[0]?.sklad_id;
    setSklNast(skladId || nej || sklady[0]?.id || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  useEffect(() => {
    if (!d || !sklNast) return;
    const n = (d.sklad_nastaveni || {})[sklNast] || {};
    setForm({
      min: n.min_zasoba || 0,
      hlidat: !!n.hlidat_minimum,
      dodavatel: d.dodavatel || "",
      lhuta: d.dodaci_lhuta_dny ?? "",
      baleni: d.baleni ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, sklNast]);

  const stornoPohybu = async (p) => {
    if (!window.confirm(`Stornovat pohyb ${p.typ.toLowerCase()} ${p.mnozstvi} ${p.mj}?`)) return;
    setErr("");
    try {
      await api.delete(`/api/sklad/pohyby/${p.id}`);
      await nacti();
      onZmena && onZmena();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const smazPolozku = async () => {
    if (!window.confirm(`Smazat položku „${d.nazev}"? Ze skladu zmizí úplně.`)) return;
    setErr("");
    try {
      await api.delete(`/api/sklad/polozky/${polozkaId}`);
      onZmena && onZmena();
      onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const ulozNastaveni = async () => {
    setSaving(true); setChybaNastaveni("");
    try {
      await api.patch(`/api/sklad/polozky/${polozkaId}/nastaveni`, {
        sklad_id: sklNast,
        min_zasoba: Number(form.min) || 0,
        hlidat_minimum: !!form.hlidat,
        dodavatel: form.dodavatel,
        dodaci_lhuta_dny: form.lhuta === "" ? null : Number(form.lhuta),
        baleni: form.baleni === "" ? null : Number(form.baleni),
      });
      await nacti();
      onZmena && onZmena();
      setMobilTab("sklad");
    } catch (e) {
      setChybaNastaveni(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (d === null) {
    return (
      <Dialog title="Skladová položka" onClose={onClose} wide testid="polozka-detail">
        <div className="p-8 flex justify-center"><Spinner /></div>
      </Dialog>
    );
  }
  if (d === false) {
    return (
      <Dialog title="Skladová položka" onClose={onClose} wide testid="polozka-detail">
        <p className="text-[13px] text-danger">Položku se nepodařilo načíst.</p>
      </Dialog>
    );
  }

  const skladNast = sklady.find((s) => s.id === sklNast);
  const zasobaNaSkladu = Number((d.zasoba_dle_skladu || {})[sklNast] || 0);
  // živý přepočet — bez čekání na uložení
  const stav = stavMinima(zasobaNaSkladu, form.min, d.mj);
  const posledniZmena = (d.historie_minima || [])[d.historie_minima?.length - 1];

  const pouzito = d.pouziti || [];
  const taby = [
    ["dostupne", "Dostupné", (d.rozpad || []).length],
    ["pouziti", "Kde se použil", pouzito.length],
    ["pohyby", "Pohyby", (d.pohyby || []).length],
  ];

  const dlazdice = (l, v, tmava) => (
    <div key={l} className={tmava ? "rounded-card bg-ink text-white p-3.5" : "card p-3.5 bg-[#fbfaf8]"}>
      <div className={`label-up ${tmava ? "text-white/50" : "text-muted"}`}>{l}</div>
      <div className="amount text-[18px] font-700 mt-1">{v}</div>
    </div>
  );

  const obsahTabu = (
    <>
      <Segment className="flex gap-1 p-1 bg-line-soft rounded-pill w-fit">
        {taby.map(([v, l, pocet]) => (
          <button data-on={tab === v} key={v} onClick={() => setTab(v)} data-testid={`polozka-tab-${v}`}
            className={`pill px-3.5 h-8 text-[12px] font-700 flex items-center gap-1.5 ${
              tab === v ? "text-white" : "text-sec"}`}>
            {l}<span className={`amount text-[10.5px] ${tab === v ? "text-white/50" : "text-muted-2"}`}>{pocet}</span>
          </button>
        ))}
      </Segment>

      {tab === "dostupne" && (
        <div className="border border-line-softer rounded-card overflow-hidden" data-testid="polozka-rozpad">
          {(d.rozpad || []).map((r) => (
            <div key={r.sklad_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-line-softer last:border-0"
              data-testid={`rozpad-${r.sklad_id}`}>
              <span className="h-1.5 w-1.5 rounded-full bg-ink shrink-0" />
              <span className="min-w-0 flex-1 text-[13px] font-600 text-ink truncate">
                {r.nazev}{r.typ === "DODAVKA" ? " (dodávka)" : ""}
              </span>
              <span className="amount text-[12px] text-muted shrink-0">{fmtKc(r.mnozstvi * (d.cena_nakup || 0))} Kč</span>
              <span className="amount text-[13px] font-700 text-ink w-[70px] text-right shrink-0">{r.mnozstvi} {d.mj}</span>
              {onPresun && (
                <button className="text-[11.5px] font-700 text-accent-hover shrink-0"
                  data-testid={`presun-z-${r.sklad_id}`}
                  onClick={() => onPresun({ polozka_id: d.id, z_skladu_id: r.sklad_id, mnozstvi: r.mnozstvi })}>
                  Převést →
                </button>
              )}
            </div>
          ))}
          {!(d.rozpad || []).length && (
            <div className="px-4 py-6 text-[12.5px] text-muted text-center">
              Na skladech nic není. {d.ceka_mnozstvi ? "Něco ale čeká na naskladnění." : ""}
            </div>
          )}
        </div>
      )}

      {tab === "pouziti" && (
        <div className="border border-line-softer rounded-card overflow-hidden" data-testid="polozka-pouziti">
          {pouzito.map((z) => (
            <div key={z.obchodni_pripad_id}
              className="flex items-center gap-3 px-4 py-2.5 border-b border-line-softer last:border-0"
              data-testid={`pouziti-${z.obchodni_pripad_id}`}>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-600 text-ink truncate">
                  {z.cislo ? `${z.cislo} · ` : ""}{z.nazev}
                </span>
                <span className="block text-[11px] text-muted amount">
                  {z.posledni}
                  {z.vraceno ? ` · vydáno ${z.vydano}, vráceno ${z.vraceno}` : ""}
                </span>
              </span>
              <span className="amount text-[12px] text-muted shrink-0">{fmtKc(z.hodnota_kc)} Kč</span>
              <span className="amount text-[13px] font-700 text-ink w-[70px] text-right shrink-0">
                {z.spotrebovano} {d.mj}
              </span>
              {/* Vrátit jde jen to, co je na zakázce ještě vydané. */}
              {onVratka && z.spotrebovano > 0 && (
                <button className="text-[11.5px] font-700 text-accent-hover shrink-0"
                  data-testid={`vratka-${z.obchodni_pripad_id}`}
                  onClick={() => onVratka({
                    polozka_id: d.id, obchodni_pripad_id: z.obchodni_pripad_id,
                    technologie_id: z.technologie_id, sklad_id: z.sklad_id, mnozstvi: z.spotrebovano,
                  })}>
                  Vrátit →
                </button>
              )}
            </div>
          ))}
          {!pouzito.length && (
            <div className="px-4 py-6 text-[12.5px] text-muted text-center">
              Tenhle materiál zatím na žádnou zakázku nešel.
            </div>
          )}
          {pouzito.length > 0 && (
            <div className="px-4 py-2.5 bg-inputbg flex items-center">
              <span className="label-up text-muted-2">Celkem do nákladů zakázek</span>
              <span className="ml-auto amount text-[13px] font-800 text-ink">{fmtKc(d.hodnota_pouziti_kc)} Kč</span>
            </div>
          )}
        </div>
      )}

      {tab === "pohyby" && (
        <div className="border border-line-softer rounded-card overflow-hidden">
          {(d.pohyby || []).length === 0
            ? <div className="px-4 py-6 text-[12.5px] text-muted text-center">Zatím žádný pohyb.</div>
            : d.pohyby.map((p) => (
              <PohybRadek key={p.id} p={p} onSmazat={muzeMazat ? () => stornoPohybu(p) : null} />
            ))}
        </div>
      )}
    </>
  );

  /** Panel nastavení — sdílený desktopem (pravý sloupec) i mobilem (záložka). */
  const panelNastaveni = (velky) => (
    <>
      {sklady.length > 1 && (
        <label className="block">
          <span className="label-up text-muted block mb-1.5">Sklad</span>
          <Vyber className="field h-9 text-[12.5px]" value={sklNast}
            onChange={(e) => setSklNast(e.target.value)} data-testid="nastaveni-sklad">
            {sklady.map((s) => (
              <option key={s.id} value={s.id}>{s.nazev}{s.typ === "DODAVKA" ? " (dodávka)" : ""}</option>
            ))}
          </Vyber>
        </label>
      )}
      <div>
        <div className="text-[13px] font-700 text-ink mb-1.5">Minimální zásoba</div>
        <MinStepper value={form.min} onChange={(v) => setForm((f) => ({ ...f, min: v }))}
          krok={Number(form.baleni) || 20} mj={d.mj} velky={velky} testid="min-stepper" />
        <div className="mt-2">
          <StavRadek stav={stav} testid="min-stav"
            extra={velky ? `na skladě ${zasobaNaSkladu} ${d.mj}` : ""} />
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Prepinac on={form.hlidat} velky={velky} testid="hlidat-prepinac"
          onChange={(v) => setForm((f) => ({ ...f, hlidat: v }))} />
        <div className="min-w-0">
          <div className="text-[13px] font-700 text-ink">Hlídat minimum</div>
          <div className="text-[11.5px] text-muted mt-0.5">
            Upozornit {user?.name?.split(" ")[0] || "tě"}, když stav klesne pod minimum.
          </div>
        </div>
      </div>
      <div className="border-t border-line-soft pt-3 space-y-2">
        {[["Dodavatel", "dodavatel", "text", ""],
          ["Dodací lhůta", "lhuta", "number", "dní"],
          ["Balení", "baleni", "number", `${d.mj} / bal`]].map(([l, k, typ, mj]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="text-[11.5px] font-500 text-sec-3 w-[92px] shrink-0">{l}</span>
            <input type={typ} value={form[k]} data-testid={`nastaveni-${k}`}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              className={`flex-1 min-w-0 text-right text-[12.5px] font-600 text-ink bg-transparent outline-none
                border-b border-transparent focus:border-line ${typ === "number" ? "amount" : ""}`} />
            {mj && <span className="amount text-[11px] text-muted shrink-0">{mj}</span>}
          </div>
        ))}
      </div>
      {posledniZmena && (
        <div className="text-[10.5px] text-muted">
          Minimum změnil {posledniZmena.jmeno || "—"} ({posledniZmena.z} → {posledniZmena.na}).
        </div>
      )}
      {chybaNastaveni && (
        <div className="card px-3 py-2.5 flex items-center gap-2" data-testid="nastaveni-chyba">
          <span className="h-1.5 w-1.5 rounded-full bg-danger shrink-0" />
          <span className="text-[11.5px] font-600 text-ink">{chybaNastaveni}</span>
        </div>
      )}
    </>
  );

  return (
    <Dialog title={d.nazev} onClose={onClose} bare sirka="md:max-w-[880px]" testid="polozka-detail"
      meta={`${d.kod || d.ean || "bez kódu"} · nákup ${fmtKc(d.cena_nakup)} Kč ${d.cenik ? "z ceníku" : "(mimo ceník)"}${skladNast ? ` · sklad ${skladNast.nazev}` : ""}`}
      footer={muzeMazat ? (
        <>
          <button onClick={smazPolozku} data-testid="smazat-polozku"
            className="text-[12.5px] font-700 text-danger inline-flex items-center gap-1.5">
            <Trash2 size={14} /> Smazat položku
          </button>
          <span className="ml-auto text-[11.5px] text-muted">
            {(d.pohyby || []).length ? "Nejdřív stornuj pohyby v poslední záložce." : "Položka nemá žádné pohyby."}
          </span>
        </>
      ) : null}>

      {/* ============ MOBIL: přepínač Sklad / Nastavení ============ */}
      <div className="md:hidden p-4 space-y-4">
        <Segment className="grid grid-cols-2 p-1 bg-[#e7e5e0] rounded-pill">
          {[["sklad", "Sklad"], ["nastaveni", "Nastavení"]].map(([v, l]) => (
            <button data-on={mobilTab === v} key={v} onClick={() => setMobilTab(v)} data-testid={`mobil-tab-${v}`}
              className={`h-11 rounded-pill text-[13.5px] font-700 ${
                mobilTab === v ? "text-white" : "text-sec-3"}`}>{l}</button>
          ))}
        </Segment>
        <div className="flex items-center gap-2">
          <span className="label-up text-muted">
            {mobilTab === "sklad" ? "Zásoba a pohyby" : "Nastavení zásoby"}
          </span>
          <span className="ml-auto" />
          <StavChip stav={stav} testid="mobil-stav-chip" />
        </div>

        {mobilTab === "sklad" ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              {dlazdice("K dispozici", <>{d.zasoba} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
              {dlazdice("Minimum", <>{Number(form.min) || 0} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
              {dlazdice("Spotřebováno", <>{d.mnozstvi_na_zakazkach || 0} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
              {dlazdice("Hodnota", `${fmtKc(d.hodnota_kc)} Kč`, true)}
            </div>
            {stav ? <ZasobaPas stav={stav} mj={d.mj} /> : (
              <div className="text-[12px] text-muted">Minimum se nehlídá.</div>
            )}
            {obsahTabu}
            <button onClick={() => setMobilTab("nastaveni")} data-testid="mobil-nastavit-minimum"
              className="w-full h-11 rounded-pill border border-line text-[13.5px] font-700 text-ink">
              Nastavit minimum
            </button>
          </>
        ) : (
          <div className="space-y-4">
            {panelNastaveni(true)}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <button onClick={() => setMobilTab("sklad")} data-testid="mobil-zrusit"
                className="h-11 rounded-pill border border-line text-[13.5px] font-700 text-ink">Zrušit</button>
              <button onClick={ulozNastaveni} disabled={saving} data-testid="mobil-ulozit"
                className="h-11 rounded-pill bg-ink text-white text-[13.5px] font-700">
                {saving ? "Ukládám…" : "Uložit"}
              </button>
            </div>
          </div>
        )}
        <Chyba text={err} testid="polozka-chyba-mazani" />
      </div>

      {/* ============ DESKTOP: obsah vlevo, panel nastavení vpravo ============ */}
      <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_296px] md:items-stretch">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {dlazdice("K dispozici", <>{d.zasoba} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
            {dlazdice("Spotřebováno", <>{d.mnozstvi_na_zakazkach || 0} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
            {dlazdice("Čeká na naskladnění", <>{d.ceka_mnozstvi || 0} <span className="text-[12px] font-500 text-sec-3">{d.mj}</span></>)}
            {dlazdice("Hodnota zásoby", `${fmtKc(d.hodnota_kc)} Kč`, true)}
          </div>
          {obsahTabu}
          <Chyba text={err} testid="polozka-chyba-mazani-md" />
        </div>
        <div className="flex flex-col gap-[18px] bg-[#fbfaf8] border-l border-line-softer p-6"
          data-testid="nastaveni-panel">
          <div className="label-up text-muted">Nastavení zásoby</div>
          {panelNastaveni(false)}
          <div className="mt-auto pt-2">
            <button onClick={ulozNastaveni} disabled={saving} data-testid="ulozit-nastaveni"
              className="w-full h-10 rounded-pill bg-ink text-white text-[13px] font-700 hover:bg-sidebar-2">
              {saving ? "Ukládám…" : "Uložit nastavení"}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

/** Fronta ohlášených, ale ještě nepřevzatých příjmů. */
export function NaskladnitDialog({ davka, onClose, onSaved }) {
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const naskladni = async () => {
    setErr(""); setSaving(true);
    try {
      await api.post("/api/sklad/naskladnit", { davka_id: davka.davka_id });
      onSaved(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title="Naskladnit" onClose={onClose} wide testid="naskladnit-dialog"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Zrušit</Button>
        <Button className="ml-auto" onClick={naskladni} disabled={saving} data-testid="naskladnit-potvrdit">
          {saving ? <Spinner /> : "Mám fyzicky na skladě"}
        </Button>
      </>}>
      <div className="text-[12.5px] text-muted">
        {davka.sklad_nazev} · {davka.datum}
        {davka.doklad?.cislo ? ` · doklad ${davka.doklad.cislo}` : ""}
        {davka.kdo ? ` · zařadil ${davka.kdo}` : ""}
      </div>
      {(davka.doklad?.soubory || []).map((s, i) => (
        <a key={i} href={souborUrl(s.url)} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-700 text-accent-hover"
          data-testid={`naskladnit-doklad-${i}`}>
          <ExternalLink size={13} /> Zobrazit {(davka.doklad.soubory || []).length > 1 ? `stranu ${i + 1}` : "doklad"}
        </a>
      ))}
      <div className="border border-line-softer rounded-card overflow-hidden">
        {(davka.radky || []).map((r) => (
          <div key={r.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-line-softer last:border-0">
            <span className="min-w-0 flex-1 text-[13px] font-600 text-ink truncate">{r.nazev}</span>
            <span className="amount text-[12.5px] text-sec-3">{r.mnozstvi} {r.mj}</span>
            <span className="amount text-[12.5px] font-700 text-ink w-[100px] text-right">{fmtKc(r.celkem)} Kč</span>
          </div>
        ))}
      </div>
      <p className="text-[11.5px] text-muted">
        Potvrzením vznikne zásoba. Do té doby materiál v zásobě není, i když je doklad zařazený.
      </p>
      <Chyba text={err} testid="naskladnit-chyba" />
    </Dialog>
  );
}

export function PolozkaDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ nazev: "", kod: "", ean: "", mj: "ks", cena_nakup: "", min_zasoba: "", umisteni: "", vyrobce: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (pole, v) => setF((x) => ({ ...x, [pole]: v }));

  const submit = async () => {
    if (!f.nazev.trim()) return setErr("Zadej název položky.");
    setErr(""); setSaving(true);
    try {
      await api.post("/api/sklad/polozky", {
        ...f, nazev: f.nazev.trim(),
        // Nula je platná hodnota (0 = nehlídat minimum), prázdné pole je nula.
        cena_nakup: Number(f.cena_nakup || 0), min_zasoba: Number(f.min_zasoba || 0),
      });
      onSaved(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title="Nová skladová položka" onClose={onClose} testid="polozka-dialog"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Zrušit</Button>
        <Button className="ml-auto" onClick={submit} disabled={saving} data-testid="polozka-ulozit">
          {saving ? <Spinner /> : "Založit"}
        </Button>
      </>}>
      <Field label="Název"><input className="field" value={f.nazev} onChange={(e) => set("nazev", e.target.value)} data-testid="polozka-nazev" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Kód"><input className="field" value={f.kod} onChange={(e) => set("kod", e.target.value)} /></Field>
        <Field label="EAN"><input className="field" value={f.ean} onChange={(e) => set("ean", e.target.value)} /></Field>
        <Field label="Měrná jednotka"><input className="field" value={f.mj} onChange={(e) => set("mj", e.target.value)} /></Field>
        <Field label="Nákupní cena" hint="Je-li v ceníku, platí cena z ceníku">
          <input className="field" inputMode="decimal" value={f.cena_nakup} onChange={(e) => set("cena_nakup", e.target.value)} />
        </Field>
        <Field label="Minimální zásoba" hint="0 = nehlídat">
          <input className="field" inputMode="decimal" value={f.min_zasoba} onChange={(e) => set("min_zasoba", e.target.value)} data-testid="polozka-min" />
        </Field>
        <Field label="Umístění"><input className="field" value={f.umisteni} onChange={(e) => set("umisteni", e.target.value)} /></Field>
      </div>
      <Field label="Dodavatel / výrobce"><input className="field" value={f.vyrobce} onChange={(e) => set("vyrobce", e.target.value)} /></Field>
      <p className="t-meta text-muted">Položka se propojí s ceníkem podle kódu, EAN nebo názvu. Když v ceníku není, založí se tam.</p>
      <Chyba text={err} testid="polozka-chyba" />
    </Dialog>
  );
}

/** Nový sklad, nebo úprava existujícího (`sklad`) — tam jde i smazat. */
export function SkladDialog({ sklad = null, onClose, onSaved }) {
  const editing = !!sklad;
  const [f, setF] = useState({ nazev: sklad?.nazev || "", typ: sklad?.typ || "SKLAD", adresa: sklad?.adresa || "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.nazev.trim()) return setErr("Zadej název skladu.");
    setErr(""); setSaving(true);
    try {
      if (editing) await api.patch(`/api/sklady/${sklad.id}`, { ...f, nazev: f.nazev.trim() });
      else await api.post("/api/sklady", { ...f, nazev: f.nazev.trim() });
      onSaved(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  // Sklad s historií pohybů server smazat nedovolí (přišli bychom o doklady),
  // jeho hláška se ukáže tady.
  const smazat = async () => {
    if (!window.confirm(`Opravdu smazat sklad „${sklad.nazev}“?`)) return;
    setErr(""); setSaving(true);
    try {
      await api.delete(`/api/sklady/${sklad.id}`);
      onSaved({ smazano: true }); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title={editing ? "Upravit sklad" : "Nový sklad"} onClose={onClose} testid="sklad-dialog"
      footer={<>
        {editing && (
          <button onClick={smazat} disabled={saving} data-testid="sklad-smazat"
            className="tap inline-flex items-center gap-1.5 t-meta font-700 text-danger hover:underline disabled:opacity-50">
            Smazat
          </button>
        )}
        <Button variant="ghost" className={editing ? "ml-auto" : ""} onClick={onClose}>Zrušit</Button>
        <Button className={editing ? "" : "ml-auto"} onClick={submit} disabled={saving} data-testid="sklad-ulozit">
          {saving ? <Spinner /> : editing ? "Uložit" : "Založit"}
        </Button>
      </>}>
      <Field label="Název"><input className="field" value={f.nazev} onChange={(e) => setF({ ...f, nazev: e.target.value })} data-testid="sklad-nazev" /></Field>
      <Field label="Typ" hint="Dodávka technika je taky sklad — materiál v autě je pořád náš.">
        <Vyber className="field" value={f.typ} onChange={(e) => setF({ ...f, typ: e.target.value })} data-testid="sklad-typ">
          <option value="SKLAD">Sklad</option>
          <option value="DODAVKA">Dodávka</option>
        </Vyber>
      </Field>
      <Field label="Adresa"><input className="field" value={f.adresa} onChange={(e) => setF({ ...f, adresa: e.target.value })} /></Field>
      <Chyba text={err} testid="sklad-chyba" />
    </Dialog>
  );
}

/** Inventura — zapisuje se skutečně napočítaný stav, backend uloží rozdíl. */
export function InventuraDialog({ sklady, onClose, onSaved }) {
  const [skladId, setSkladId] = useState(sklady[0]?.id || "");
  const [polozky, setPolozky] = useState([]);
  const [stav, setStav] = useState({});
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!skladId) { setPolozky([]); return; }
    api.get("/api/sklad/polozky", { params: { sklad_id: skladId } })
      .then(({ data }) => { setPolozky(data.items || []); setStav({}); })
      .catch(() => setPolozky([]));
  }, [skladId]);

  const submit = async () => {
    // Prázdné pole = nepočítáno, ne nula. Nulu musí uživatel napsat.
    const radky = polozky
      .filter((p) => (stav[p.id] ?? "") !== "")
      .map((p) => ({ polozka_id: p.id, skutecnost: Number(stav[p.id]) }));
    if (!radky.length) return setErr("Vyplň napočítaný stav aspoň u jedné položky.");
    setErr(""); setSaving(true);
    try {
      await api.post("/api/sklad/inventura", { sklad_id: skladId, radky });
      onSaved(); onClose();
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog title="Inventura" onClose={onClose} wide testid="inventura-dialog"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Zrušit</Button>
        <Button className="ml-auto" onClick={submit} disabled={saving} data-testid="inventura-ulozit">
          {saving ? <Spinner /> : "Srovnat stav"}
        </Button>
      </>}>
      <SkladSelect sklady={sklady} value={skladId} onChange={setSkladId} testid="inventura-sklad" />
      <div className="border border-line-softer rounded-card overflow-hidden">
        {polozky.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-line-softer last:border-0">
            <span className="min-w-0 flex-1 text-[13px] font-600 text-ink truncate">{p.nazev}</span>
            <span className="amount text-[12px] text-muted shrink-0">evidováno {p.zasoba} {p.mj}</span>
            <input className="field h-9 w-[110px] shrink-0" inputMode="decimal" placeholder="napočítáno"
              value={stav[p.id] ?? ""} data-testid={`inventura-${p.id}`}
              onChange={(e) => setStav((s) => ({ ...s, [p.id]: e.target.value }))} />
          </div>
        ))}
        {!polozky.length && <div className="px-4 py-6 text-[12.5px] text-muted text-center">Na tomto skladě nic není.</div>}
      </div>
      <p className="text-[11.5px] text-muted">Zapíše se rozdíl — historie pohybů zůstane.</p>
      <Chyba text={err} testid="inventura-chyba" />
    </Dialog>
  );
}
